import {
  TaskTrackerPermissionKey,
  hasTaskTrackerPermission,
  TASK_TRACKER_CURRENT_USER_NO_BOARD_ACCESS_TOOLTIP,
  TASK_TRACKER_PERMISSION_OPTIONS,
} from './taskTracker.permissions.constants'
import type {
  TaskTrackerProjectMember,
  TaskTrackerProjectRole,
} from '../store/taskTracker.types'
import { PROJECT_SYSTEM_ROLE_TITLES } from './projectMember.utils'

const TASK_TRACKER_PROJECT_ADMIN_PERMISSIONS: TaskTrackerPermissionKey[] =
  TASK_TRACKER_PERMISSION_OPTIONS.map((option) => option.value)

const resolveMemberRolePermissions = (
  member: TaskTrackerProjectMember,
  projectRoles: TaskTrackerProjectRole[],
): string[] => {
  const directPermissions = member.role?.permissions
  if (Array.isArray(directPermissions) && directPermissions.length > 0) {
    return directPermissions
  }

  const roleId = member.role?.id ?? member.roleId
  if (!Number.isFinite(roleId) || roleId <= 0) return []

  const roleFromProject = projectRoles.find((role) => role.id === roleId)
  return roleFromProject?.permissions ?? []
}

export const isTaskTrackerCurrentUserProjectAdmin = (
  members: TaskTrackerProjectMember[],
  currentUserId: number | null,
  projectRoles: TaskTrackerProjectRole[] = [],
): boolean => {
  if (currentUserId == null) return false

  const member = members.find((item) => item.userId === currentUserId)
  if (!member) return false

  if (member.role?.title === PROJECT_SYSTEM_ROLE_TITLES.admin) return true

  const roleId = member.role?.id ?? member.roleId
  if (!Number.isFinite(roleId) || roleId <= 0) return false

  const role = projectRoles.find((item) => item.id === roleId)
  return role?.title === PROJECT_SYSTEM_ROLE_TITLES.admin
}

export const resolveTaskTrackerCurrentUserPermissions = (
  members: TaskTrackerProjectMember[],
  projectRoles: TaskTrackerProjectRole[],
  currentUserId: number | null,
): string[] => {
  if (currentUserId == null) return []

  const member = members.find((item) => item.userId === currentUserId)
  if (!member) return []

  if (isTaskTrackerCurrentUserProjectAdmin(members, currentUserId, projectRoles)) {
    return TASK_TRACKER_PROJECT_ADMIN_PERMISSIONS
  }

  return resolveMemberRolePermissions(member, projectRoles)
}

export const currentUserHasTaskTrackerPermission = (
  members: TaskTrackerProjectMember[],
  projectRoles: TaskTrackerProjectRole[],
  currentUserId: number | null,
  permission: TaskTrackerPermissionKey,
) =>
  hasTaskTrackerPermission(
    resolveTaskTrackerCurrentUserPermissions(
      members,
      projectRoles,
      currentUserId,
    ),
    permission,
  )

export const isTaskTrackerCurrentUserProjectMember = (
  members: TaskTrackerProjectMember[],
  currentUserId: number | null,
) => {
  if (currentUserId == null) return false

  return members.some((member) => member.userId === currentUserId)
}

export const getTaskTrackerPermissionDisabledTooltip = ({
  projectId,
  permissionsLoading,
  isCurrentUserProjectParticipant,
  hasPermission,
  permissionDisabledTooltip,
  hasBoardAccess = true,
}: {
  projectId: number | null
  permissionsLoading: boolean
  isCurrentUserProjectParticipant: boolean
  hasPermission: boolean
  permissionDisabledTooltip: string
  hasBoardAccess?: boolean
}): string | null => {
  if (projectId == null || permissionsLoading || hasPermission) return null
  if (!isCurrentUserProjectParticipant) return null

  if (!hasBoardAccess) {
    return TASK_TRACKER_CURRENT_USER_NO_BOARD_ACCESS_TOOLTIP
  }

  return permissionDisabledTooltip
}
